import React, { useState, useRef, useEffect } from 'react';
import s3Storage from '@happylife-a/s3-storage';
import webApp from '@happylife-a/web-app';
import { Box, Flex } from '@chakra-ui/react';
import { useScreenSize } from '../../../contexts';
import { theme } from '../../../theme';
import S3Image from '../../S3Image';
import S3Video from '../../S3Video';
import SliderController from '../../SliderController';
import ImageZoom from '../../ImageZoom';

const MAX_THUMBNAILS_COUNT = 4;
const scrollbarThumbWidth = 2;
const scrollbarTrackWidth = 3;

function getSizeNameByScreen() {
  // @TODO: check and add size names based on screen
  return 'md';
}

function getSize(size) {
  if (!size || typeof size === 'number') {
    return size;
  }
  return size[getSizeNameByScreen()] || size.base || undefined;
}

export default function SliderV2({
  imagesAndVideos,
  mainBoxH,
  mainBoxW,
  footerBoxW,
  borderRadius = '10px',
  itemW = '90px',
  itemH = '100px',
  itemGap = '16px',
}) {
  const thumbnailContainer = useRef(null);
  const thumbItemContainer = useRef(null);
  const mainBoxRef = useRef(null);

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const thumbItemWidth = 105;

  const thumbnailWidth = !(imagesAndVideos.length <= MAX_THUMBNAILS_COUNT + 1)
    ? `${imagesAndVideos.length * thumbItemWidth + 1}px`
    : 'full';

  const onSlideChange = (index) => {
    setActiveSlideIndex(index);
  };

  const nextSlide = () => {
    setActiveSlideIndex((oldActiveSlideIndex) => {
      if (oldActiveSlideIndex + 1 === imagesAndVideos.length) {
        thumbnailContainer.current.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });

        return 0;
      }

      if (oldActiveSlideIndex >= MAX_THUMBNAILS_COUNT - 1) {
        // eslint-disable-next-line no-unused-expressions
        thumbnailContainer.current.scrollTo({
          top: 0,
          left: thumbnailContainer.current.scrollLeft + thumbItemWidth + 1,
          behavior: 'smooth',
        });
      }

      return oldActiveSlideIndex + 1;
    });
  };
  const prevSlide = () => {
    setActiveSlideIndex((oldActiveSlideIndex) => {
      if (activeSlideIndex - 1 === -1) {
        thumbnailContainer.current.scrollTo({
          top: 0,
          left: (imagesAndVideos.length - MAX_THUMBNAILS_COUNT) * thumbItemWidth,
          behavior: 'smooth',
        });
        return imagesAndVideos.length - 1;
      }
      thumbnailContainer.current.scrollTo({
        top: 0,
        left: thumbnailContainer.current.scrollLeft - thumbItemWidth,
        behavior: 'smooth',
      });

      return oldActiveSlideIndex - 1 === -1 ? imagesAndVideos.length : oldActiveSlideIndex - 1;
    });
  };

  // Reset to first slide when images change (e.g. variant switch)
  useEffect(() => {
    setActiveSlideIndex(0);
  }, [imagesAndVideos]);

  const screenSize = useScreenSize();

  const activeItem = imagesAndVideos?.[activeSlideIndex];
  const imageSrc = s3Storage.helpers.read.buildUrlProduct(activeItem?.preview);

  if (!activeItem) {
    return null;
  }

  return (
    <Box flex={1}>
      <Box
        maxH={{ base: 'auto', md: mainBoxH }}
        pos="relative"
        borderRadius={borderRadius}
        ref={mainBoxRef}
        className="slider-main-div"
        cursor="pointer"
      >
        {imagesAndVideos?.length > 1 && screenSize.isMinTablet && (
          <SliderController
            variant="variant2"
            prevSlide={prevSlide}
            nextSlide={nextSlide}
            borderRadius={borderRadius}
          />
        )}

        {screenSize.isMinTablet ? (
          <>
            {activeItem?.type === 'image' ? (
              <Flex justifyContent="center">
                <ImageZoom
                  src={imageSrc}
                  alt="Product Image"
                  width={getSize(mainBoxW)}
                  height={getSize(mainBoxH)}
                  borderRadius={borderRadius}
                  zoomScale={2.5}
                  lensColor={`${theme.colors.primary[500]}33`}
                  lensSize={120}
                  zoomPosition="auto"
                />
              </Flex>
            ) : (
              <Flex
                position="relative"
                justifyContent="center"
                overflow="hidden"
                borderRadius={borderRadius}
                minWidth={getSize(mainBoxW)}
                height={getSize(mainBoxH)}
              >
                <S3Video
                  folder={S3Video.folders.FOLDER_PRODUCTS}
                  videoKey={activeItem.source}
                  poster={activeItem.poster}
                  posterSize="@0.5"
                />
              </Flex>
            )}
          </>
        ) : (
          <webApp.components.Slider
            variant="variant4"
            imagesAndVideos={imagesAndVideos}
            onSlideChange={onSlideChange}
            activeSlideIndex={activeSlideIndex}
            itemW={itemW}
            itemH={itemH}
            mainBoxH={mainBoxH}
            mainBoxW={mainBoxW}
          />
        )}
      </Box>

      <Box
        w={footerBoxW}
        overflowY="hidden"
        overflowX="auto"
        ref={thumbnailContainer}
        css={{
          '&::-webkit-scrollbar': {
            width: `${scrollbarThumbWidth}px`,
          },
          '&::-webkit-scrollbar-track': {
            width: `${scrollbarTrackWidth}px`,
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: `${2 * scrollbarTrackWidth}px`,
          },
        }}
      >
        <Flex ref={thumbItemContainer} minW={thumbnailWidth} mt={2} gap={itemGap}>
          {imagesAndVideos?.map((imageOrVideo, idx) => (
            <Box
              key={`product-image-${idx}`}
              minW={itemW}
              maxW={itemW}
              w={itemW}
              minH={itemH}
              maxH={itemH}
              h={itemH}
              borderColor={idx === activeSlideIndex ? 'primary.500' : 'transparent'}
              borderWidth={2}
              transition="border 200ms"
              onClick={() => onSlideChange(idx)}
              overflow="hidden"
              cursor="pointer"
              borderRadius="10px"
            >
              <S3Image
                folder={S3Image.folders.FOLDER_PRODUCTS}
                imageKey={imageOrVideo.preview}
                size="@0.5"
                alt="Product Image"
                objectFit="cover"
                w="100%"
                h="100%"
              />
            </Box>
          ))}
        </Flex>
      </Box>
    </Box>
  );
}
